import { Helmet } from 'react-helmet-async'

type HeadProps = {
  title?: string
  description?: string
  keywords?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  ogUrl?: string
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player'
  canonicalUrl?: string
  noIndex?: boolean
}

const DEFAULT_TITLE = 'NBCU International Networks'
const DEFAULT_DESCRIPTION =
  'Official website for NBC Universal International Networks España S.L.U. - Legal information for Bravo International, E! International and other NBCU network brands.'
const DEFAULT_KEYWORDS =
  'NBC Universal, NBCU International Networks, Bravo International, E! International, NBCUniversal España, legal information'
const DEFAULT_OG_IMAGE = '/og-image.jpg'
const SITE_URL = 'https://www.nbcuintnetworks.com'

export function Head({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  ogTitle,
  ogDescription,
  ogImage = DEFAULT_OG_IMAGE,
  ogUrl,
  twitterCard = 'summary_large_image',
  canonicalUrl,
  noIndex = false,
}: HeadProps) {
  const fullTitle =
    title === DEFAULT_TITLE ? title : `${title} | ${DEFAULT_TITLE}`
  const finalOgTitle = ogTitle || fullTitle
  const finalOgDescription = ogDescription || description
  const finalOgUrl = ogUrl || canonicalUrl || SITE_URL
  const finalCanonicalUrl = canonicalUrl || SITE_URL

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      {!noIndex && <meta name="robots" content="index,follow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={finalCanonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={finalOgUrl} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="NBCU International Networks" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={finalOgUrl} />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional SEO */}
      <meta name="author" content="NBC Universal International Networks" />
      <meta name="publisher" content="NBC Universal International Networks" />

      {/* Language */}
      <meta httpEquiv="content-language" content="en" />
      <meta name="language" content="English" />
    </Helmet>
  )
}

export default Head
